/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import { jsx } from '@compiled/react';

import type { ModalState } from './usePermission';

import ModalDialog, {
	ModalTransition,
	ModalFooter as AKModalFooter,
	ModalBody,
	ModalTitle,
	ModalHeader,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { functionWithFG } from '@atlaskit/platform-feature-flags-react';
import { Box, Text, Stack, Inline, Flex } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { defineMessages, useIntl } from 'react-intl-next';

const styles = cssMap({
	titleContainer: {
		marginBottom: token('space.400'),
	},
	modalFooterContainer: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
	disclaimerContainer: {
		marginTop: token('space.400'),
		marginBottom: token('space.400'),
	},
	rowContainer: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
	},
});

const messages = defineMessages({
	declineButtonLabel: {
		id: 'forge-ui.adminConsentModal.decline.button.label',
		defaultMessage: 'Decline',
		description: 'Label for the decline button',
	},
	approveButtonLabel: {
		id: 'forge-ui.adminConsentModal.approve.button.label',
		defaultMessage: 'Approve',
		description: 'Label for the approve button',
	},
	noticeForMoreDetails: {
		id: 'forge-ui.adminConsentModal.notice.for-more-details',
		defaultMessage: 'For more details, refer to {appName}\'s',
		description: 'Message for the notice part 1',
	},
	noticeAnd: {
		id: 'forge-ui.adminConsentModal.notice.and',
		defaultMessage: 'and',
		description: 'Message for the notice part 2',
	},
	termsOfUseLink: {
		id: 'forge-ui.adminConsentModal.terms-of-use.link',
		defaultMessage: 'terms of use',
		description: 'Label for the terms of use link',
	},
	privacyPolicyLink: {
		id: 'forge-ui.adminConsentModal.privacy-policy.link',
		defaultMessage: 'privacy policy',
		description: 'Label for the privacy policy link',
	},
	modalTransitionTitle: {
		id: 'forge-ui.adminConsentModal.modal-transition.title',
		defaultMessage: 'Data storage, sharing, receiving and processing',
		description: 'Title for the modal transition',
	},
	modalTransitionText: {
		id: 'forge-ui.adminConsentModal.modal-transition.text',
		defaultMessage: '{appName} is requesting to share with and receive data from the following domains:',
		description: 'Text for the modal transition',
	},
});

const useConditionalIntl = functionWithFG<() => any>(
	'forge-ui-add-use-intl-to-format-messages',
	useIntl,
	() => ({
		formatMessage: (_: any, valuesOrFallback?: any, fallback?: string): string => {
			if (typeof valuesOrFallback === 'string') {
				return valuesOrFallback;
			}
			return fallback ?? '';
		}
	}),
);

export const AdminModal = ({ modalState }: { modalState: ModalState }) => {
	const intl = useConditionalIntl();

	if (modalState.state === 'closed') {
		return <ModalTransition />;
	}

	const ModalFooter = () => {
		return (
			<Box xcss={styles.modalFooterContainer}>
				<AKModalFooter>
					<Inline space="space.075">
						<Button onClick={modalState.onCancel}>
							{intl.formatMessage(messages.declineButtonLabel, 'Decline')}
						</Button>
						<Button onClick={modalState.onConfirm} appearance="primary">
							{intl.formatMessage(messages.approveButtonLabel, 'Approve')}
						</Button>
					</Inline>
				</AKModalFooter>
			</Box>
		);
	};

	const renderDomains = () => {
		const getHumanReadableName = (operation: string) => {
			switch (operation) {
				case 'FETCH_BACKEND_SIDE':
					return 'Fetch backend side';
				case 'FETCH_CLIENT_SIDE':
					return 'Fetch client side';
				case 'FONTS':
					return 'Fonts';
				case 'FRAMES':
					return 'Frames';
				case 'IMAGES':
					return 'Images';
				case 'MEDIA':
					return 'Media';
				case 'NAVIGATION':
					return 'Navigation';
				case 'SCRIPTS':
					return 'Scripts';
				case 'STYLES':
					return 'Styles';
				default:
					return 'Egress';
			}
		};

		const domainInfo = modalState.domainInfo;

		if (Object.keys(domainInfo).length === 0) {
			return null;
		}

		if (domainInfo.type === 'REMOTE') {
			return (
				<Flex direction="column" gap="space.150">
					{domainInfo.payload.remotes.map((r) => {
						const manifestRemoteInfo = domainInfo.manifestRemoteMap[r.key];

						return (
							<Stack key={r.key} space="space.150">
								<Heading size="small">
									{manifestRemoteInfo ? manifestRemoteInfo.description : 'Remote'}
								</Heading>
								<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
									<Text>
										{r.configured.endpoint === null
											? 'This remote is being reset to the default value'
											: r.configured.endpoint}
									</Text>
									<Text>{manifestRemoteInfo ? manifestRemoteInfo.name : null}</Text>
								</Inline>
							</Stack>
						);
					})}
				</Flex>
			);
		}

		if (domainInfo.type === 'EGRESS') {
			return (
				<Flex direction="column" gap="space.150">
					{domainInfo.payload.egresses.map((e) => (
						<Stack key={e.key} space="space.150">
							<Heading size="xsmall">{e.description}</Heading>
							{e.configured.map((c) => {
								return c.type.map((t) => {
									return (
										<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
											<Text>{c.domain}</Text>
											<Text>{getHumanReadableName(t)}</Text>
										</Inline>
									);
								});
							})}
						</Stack>
					))}
				</Flex>
			);
		}
	};

	const Notice = () => {
		const { appName, termsOfUseLink, privacyPolicyLink } = modalState;
		if (termsOfUseLink && privacyPolicyLink) {
			return (
				<Text>
					{intl!.formatMessage(messages.noticeForMoreDetails, { appName: appName ?? 'the app' }, `For more details, refer to ${appName}\'s`)}
					{' '}
					<Link href={termsOfUseLink}>
						{intl!.formatMessage(messages.termsOfUseLink, 'terms of use')}
					</Link>
					{' '}
					{intl!.formatMessage(messages.noticeAnd, 'and')}
					{' '}
					<Link href={privacyPolicyLink}>
						{intl!.formatMessage(messages.privacyPolicyLink, 'privacy policy')}
					</Link>
				</Text>
			);
		} else if (termsOfUseLink) {
			return (
				<Text>
					{intl!.formatMessage(messages.noticeForMoreDetails, { appName: appName ?? 'the app' }, `For more details, refer to ${appName}\'s`)}
					{' '}
					<Link href={termsOfUseLink}>
						{intl!.formatMessage(messages.termsOfUseLink, 'terms of use')}
					</Link>
				</Text>
			);
		} else if (privacyPolicyLink) {
			return (
				<Text>
					{intl!.formatMessage(messages.noticeForMoreDetails, { appName: appName ?? 'the app' }, `For more details, refer to ${appName}\'s`)}
					{' '}
					<Link href={privacyPolicyLink}>
						{intl!.formatMessage(messages.privacyPolicyLink, 'privacy policy')}
					</Link>
				</Text>
			);
		}
	};

	return (
		<ModalTransition>
			<ModalDialog
				onClose={() => {
					modalState.onCancel();
				}}
				width="large"
			>
				<ModalHeader>
					<Box xcss={styles.titleContainer}>
						<ModalTitle>
							{intl!.formatMessage(messages.modalTransitionTitle, 'Data storage, sharing, receiving and processing')}
						</ModalTitle>
					</Box>
				</ModalHeader>
				<ModalBody>
					<Stack space="space.300">
						<Text>
							{intl!.formatMessage(messages.modalTransitionText, { appName: modalState.appName ?? 'The app' }, `${modalState.appName ?? 'The app'} is requesting to share with and receive data from the following domains:`)}
						</Text>
						<Box>
							{renderDomains()}
							<Box xcss={styles.disclaimerContainer}>
								<Notice />
							</Box>
						</Box>
					</Stack>
				</ModalBody>
				<ModalFooter />
			</ModalDialog>
		</ModalTransition>
	);
};
